import { isTauriRuntime } from "./runtime";
import { readBrowserMediaOriginalBlob, readBrowserMediaUrl, storeBrowserOriginalMedia } from "./browserSecureMedia";

const MAX_MEDIA_BYTES = 20 * 1024 * 1024;
const MAX_MEDIA_PER_MEMORY = 8;
const SUPPORTED_PHOTO_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/avif",
  "image/heic",
  "image/heif"
]);

export interface PreparedMemoryMedia {
  id: string;
  file: File;
  nativeCopy: boolean;
  sha256?: string;
}

interface NativeMediaCopy {
  mediaId: string;
  byteSize: number;
  sha256: string;
  storageState: "durable";
}

interface NativeMediaBytes {
  bytes: number[];
  mimeType: string;
}

export interface LocalMediaIntegrity {
  checkedCopies: number;
  healthyCopies: number;
  unhealthyCopies: number;
  durableBytes: number;
  pendingRelayUploads: number;
}

interface NativeMediaWrite {
  mediaId: string;
  fileName: string;
  mimeType: string;
  bytes: number[];
  previewBytes?: number[];
  previewMimeType?: string;
}

export function isSupportedPhoto(file: Pick<File, "type" | "size">) {
  return SUPPORTED_PHOTO_TYPES.has(file.type) && file.size > 0 && file.size <= MAX_MEDIA_BYTES;
}

function persistenceError(file: File) {
  if (!SUPPORTED_PHOTO_TYPES.has(file.type)) {
    return `${file.name} 的格式暂不支持，请选择 JPG、PNG、WebP、GIF、AVIF 或 HEIC 照片`;
  }
  if (file.size === 0) return `${file.name} 是空文件`;
  if (file.size > MAX_MEDIA_BYTES) return `${file.name} 超过 20MB，请先压缩后再添加`;
  return null;
}

function blobToBytes(blob: Blob) {
  return blob.arrayBuffer().then((buffer) => Array.from(new Uint8Array(buffer)));
}

function canvasBlob(canvas: HTMLCanvasElement) {
  return new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.84));
}

async function createPreview(file: File) {
  if (typeof createImageBitmap !== "function") return null;
  let bitmap: ImageBitmap | undefined;
  try {
    bitmap = await createImageBitmap(file);
    const largestEdge = Math.max(bitmap.width, bitmap.height);
    const scale = largestEdge > 640 ? 640 / largestEdge : 1;
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext("2d", { alpha: false });
    if (!context) return null;
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await canvasBlob(canvas);
    if (!blob || blob.size === 0) return null;
    return { bytes: await blobToBytes(blob), mimeType: "image/jpeg" };
  } catch {
    return null;
  } finally {
    bitmap?.close();
  }
}

function checkedNativeCopies(copies: NativeMediaCopy[], expectedIds: string[]) {
  if (copies.length !== expectedIds.length) {
    throw new Error("本机媒体账本没有完整确认所有照片");
  }
  const byId = new Map(copies.map((copy) => [copy.mediaId, copy]));
  return expectedIds.map((mediaId) => {
    const copy = byId.get(mediaId);
    if (!copy || copy.storageState !== "durable" || copy.byteSize <= 0 || !/^[a-f0-9]{64}$/i.test(copy.sha256)) {
      throw new Error("本机媒体账本确认无效");
    }
    return copy;
  });
}

export async function prepareMemoryMedia(files: File[]): Promise<PreparedMemoryMedia[]> {
  if (files.length === 0 || files.length > MAX_MEDIA_PER_MEMORY) {
    throw new Error(`一次请添加 1 至 ${MAX_MEDIA_PER_MEMORY} 张照片`);
  }

  for (const file of files) {
    const error = persistenceError(file);
    if (error) throw new Error(error);
  }

  const drafts = files.map((file) => ({ id: crypto.randomUUID(), file }));
  if (!isTauriRuntime()) {
    const copies = await Promise.all(drafts.map(({ id, file }) => storeBrowserOriginalMedia(id, file)));
    return drafts.map((draft) => ({
      id: draft.id,
      file: draft.file,
      nativeCopy: true,
      sha256: copies.find((copy) => copy.mediaId === draft.id)?.sha256
    }));
  }

  const { invoke } = await import("@tauri-apps/api/core");
  const copies: NativeMediaCopy[] = [];
  for (const draft of drafts) {
    const preview = await createPreview(draft.file);
    // Keep the Android WebView bridge bounded to one original at a time.
    const items: NativeMediaWrite[] = [{
      mediaId: draft.id,
      fileName: draft.file.name,
      mimeType: draft.file.type,
      bytes: await blobToBytes(draft.file),
      ...(preview ? { previewBytes: preview.bytes, previewMimeType: preview.mimeType } : {})
    }];
    copies.push(
      ...checkedNativeCopies(
        await invoke<NativeMediaCopy[]>("store_local_media", { request: { items } }),
        [draft.id]
      )
    );
  }
  return drafts.map((draft) => ({
    id: draft.id,
    file: draft.file,
    nativeCopy: true,
    sha256: copies.find((copy) => copy.mediaId === draft.id)?.sha256
  }));
}

async function readNativeMediaUrl(mediaId: string, command: "read_local_media_preview" | "read_local_media_original") {
  if (!isTauriRuntime()) {
    try {
      return await readBrowserMediaUrl(mediaId);
    } catch {
      // A browser cache can be unavailable or cleared independently of the
      // cloud metadata. Treat that as a missing local copy, never as a reason
      // to fail loading the whole galaxy.
      return null;
    }
  }
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    const media = await invoke<NativeMediaBytes>(command, { mediaId });
    if (!Array.isArray(media.bytes) || !SUPPORTED_PHOTO_TYPES.has(media.mimeType)) return null;
    return URL.createObjectURL(new Blob([new Uint8Array(media.bytes)], { type: media.mimeType }));
  } catch {
    return null;
  }
}

export function readLocalMediaPreviewUrl(mediaId: string) {
  return readNativeMediaUrl(mediaId, "read_local_media_preview");
}

export function readLocalMediaOriginalUrl(mediaId: string) {
  return readNativeMediaUrl(mediaId, "read_local_media_original");
}

export async function readLocalMediaOriginalBlob(mediaId: string) {
  if (!isTauriRuntime()) return readBrowserMediaOriginalBlob(mediaId);
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    const media = await invoke<NativeMediaBytes>("read_local_media_original", { mediaId });
    if (!Array.isArray(media.bytes) || !SUPPORTED_PHOTO_TYPES.has(media.mimeType)) return null;
    return new Blob([new Uint8Array(media.bytes)], { type: media.mimeType });
  } catch {
    return null;
  }
}

export async function inspectLocalMediaIntegrity(): Promise<LocalMediaIntegrity | null> {
  if (!isTauriRuntime()) return null;
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    const result = await invoke<LocalMediaIntegrity>("inspect_local_media_integrity");
    if (!Number.isInteger(result.checkedCopies) || !Number.isInteger(result.unhealthyCopies)) return null;
    return result;
  } catch {
    return null;
  }
}
